import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, TrendingUp, ShieldCheck, ArrowDown } from "lucide-react";
import { fmtMoney, DEFAULT_CURRENCY } from "../../lib/money";

/**
 * QENVA · Prédictions Guardian™ — simulation block.
 *
 *  ┌──────────────────────────────────────────────────────────────┐
 *  │ ✨ Prédictions Guardian™                                       │
 *  │ Si les alertes critiques sont corrigées :                     │
 *  │                                                                │
 *  │  Score futur        Valeur ajoutée       Exposition réduite   │
 *  │   93 /100             +127 400 $              -31 %           │
 *  └──────────────────────────────────────────────────────────────┘
 */

export default function SimulationPredictions({ simulation, testId = "simulation-predictions" }) {
  const { t } = useTranslation("dashboard");
  const sim = simulation || {};
  const currency = (sim.currency || DEFAULT_CURRENCY).toUpperCase();

  return (
    <section
      className="rounded-2xl p-6 sm:p-7 border border-qenva-blue/15"
      style={{
        background: "linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)",
      }}
      data-testid={testId}
    >
      <header className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-qenva-blue/10 grid place-items-center text-qenva-blue">
          <Sparkles size={17} strokeWidth={1.75} />
        </div>
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-qenva-blue">
            {t("simulation.kicker")}
          </p>
          <h2 className="font-display font-bold text-qenva-navy text-[15px] tracking-tight">
            {t("simulation.title")}
          </h2>
        </div>
      </header>

      <div className="grid sm:grid-cols-3 gap-4">
        <Stat
          icon={ShieldCheck}
          iconClass="bg-emerald-100 text-emerald-600"
          label={t("simulation.future_score")}
          value={`${sim.future_score ?? "—"}`}
          unit="/100"
          valueClass="text-emerald-600"
          testId={`${testId}-future-score`}
        />
        <Stat
          icon={TrendingUp}
          iconClass="bg-emerald-100 text-emerald-600"
          label={t("simulation.value_added")}
          value={`+${fmtMoney(sim.value_added, currency)}`}
          valueClass="text-emerald-600"
          testId={`${testId}-value-added`}
        />
        <Stat
          icon={ArrowDown}
          iconClass="bg-rose-100 text-rose-600"
          label={t("simulation.exposure_reduction")}
          value={`-${sim.exposure_reduction_pct ?? 0}`}
          unit="%"
          valueClass="text-rose-600"
          testId={`${testId}-exposure-reduction`}
        />
      </div>
    </section>
  );
}

function Stat({ icon: Icon, iconClass, label, value, unit, valueClass, testId }) {
  return (
    <div className="rounded-xl bg-white border border-white/60 p-4 shadow-sm" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${iconClass}`}>
        <Icon size={16} strokeWidth={1.75} />
      </div>
      <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-1">{label}</p>
      <p className={`font-display font-bold text-[26px] tabular-nums leading-none ${valueClass}`}>
        {value}
        {unit && <span className="text-[14px] text-zinc-400 font-normal ml-1">{unit}</span>}
      </p>
    </div>
  );
}
